import React, { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { COMMON_PROCESSING_MESSAGE, SUCCESS_UPDATED } from "../../data/message";
import { setLoader, unsetLoader } from "../../features/loader/loaderSlice";
import { error, success, info as toastInfo } from "../../features/toast/toastSlice";
import licenceService from "../../services/licence.service";
import { fetchLicenceData } from "../../features/licence/licenceSlice";
import LicenceForm from "../../components/LicenceForm";
import PartnerApi from "../../helpers/PartnerApi";
import ActivationModal from "./ActivationModal";
import EditButtonSales from "../../admin/licence/EditButtonSales";
import EditButtonBackUp from "../../admin/licence/EditButtonBackUp";
import EditButtonSignAgreement from "../../admin/licence/EditButtonSignAgreement";
import TabActivationFolderButton from "../../components/buttons/TabActivationFolder";
import { TabActionButtons } from "../../components/buttons";
import TabHome from "../../components/TabLicenceEdit/TabHome";
import Tab from "../../components/TabLicenceEdit/Tab";
import CustomerDetailList from "../../components/TabLicenceEdit/pages/CustomerDetailList";
import InstallationDetailList from "../../components/TabLicenceEdit/pages/InstallationDetails";
import NoteList from "../../components/TabLicenceEdit/pages/NoteList";
import LicenceList from "../../components/TabLicenceEdit/pages/LicenceList";

const normalizeLicenceResponse = (lic = {}) => {
  const noteList =
    Array.isArray(lic.noteList) ? lic.noteList
      : Array.isArray(lic.licenseNoteList) ? lic.licenseNoteList
        : Array.isArray(lic.licenceNoteList) ? lic.licenceNoteList
          : Array.isArray(lic.notes) ? lic.notes
            : Array.isArray(lic.note) ? lic.note
              : [];

  const customerList =
    Array.isArray(lic.customerList) ? lic.customerList
      : Array.isArray(lic.customers) ? lic.customers
        : Array.isArray(lic.customerDetailList) ? lic.customerDetailList
          : Array.isArray(lic.customerInfoList) ? lic.customerInfoList
            : Array.isArray(lic.ownerList) ? lic.ownerList
              : [];

  const installList =
    Array.isArray(lic.installList) ? lic.installList
      : Array.isArray(lic.installationList) ? lic.installationList
        : Array.isArray(lic.installationDetailList) ? lic.installationDetailList
          : Array.isArray(lic.installations) ? lic.installations
            : [];

  const licenseId = lic.licenseId ?? lic.id ?? lic.licenceId ?? null;

  return {
    ...lic,
    id: lic.id ?? licenseId ?? lic.id,
    licenseId,
    noteList,
    customerList,
    installList,
  };
};

const EditLicence = ({ id, tabId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const params = useParams();
  const licenceId = id ?? params?.id;

  // ✅ tabId prop gelmezse (route) kapatma bozulmasın
  const safeTabId = useMemo(() => {
    if (tabId) return tabId;
    const num = Number(licenceId);
    if (Number.isFinite(num) && num > 0) return `licence-edit:${num}`;
    return "0";
  }, [tabId, licenceId]);

  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState("1");

  const [data, setData] = useState({
    licenseNo: "",
    productCode: "",
    noteList: [],
    customerList: [],
    installList: [],
  });

  const [errors, setErrors] = useState({});

  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const [base64File, setBase64File] = useState("");
  const [selectedPrmName, setSelectedPrmName] = useState("");

  const queryData = useSelector((state) => state.Licences?.queryData) ?? {};
  const { pageIndex, pageSize, filter } = queryData;

  const formRef = useRef();

  const items = [
    { name: t("Licence"), id: "1" },
    { name: t("Customer"), id: "2" },
    { name: t("Installation"), id: "3" },
    { name: t("Note"), id: "4" },
  ];

  useEffect(() => {
    let alive = true;

    const getLicenceData = async (licId) => {
      try {
        dispatch(setLoader({ message: t(COMMON_PROCESSING_MESSAGE), delay: 5000 }));

        const res = await licenceService.getLicence({ id: licId });
        const raw = res?.license ?? res?.data?.license ?? res?.data ?? res ?? {};
        const normalized = normalizeLicenceResponse(raw);

        if (alive) setData(normalized);
      } catch (err) {
        const msg =
          err?.response?.data?.error?.more ||
          err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          t("ErrorOccurred");

        dispatch(error({ title: "Error", message: msg, delay: 6000 }));
      } finally {
        dispatch(unsetLoader());
      }
    };

    if (licenceId) getLicenceData(licenceId);

    return () => {
      alive = false;
    };
  }, [licenceId, dispatch, t]);

  const closeModal = () => {
    setModalVisible(false);
    setBase64File("");
    setSelectedPrmName("");
  };

  const handleError = (field) => {
    const newErr = {};
    Object.keys(errors).forEach((key) => {
      if (key !== field) newErr[key] = errors[key];
    });
    return newErr;
  };

  const onChange = (e) => {
    let { name, value, type, checked } = e.target;
    value = type === "checkbox" ? (checked ? 1 : 0) : value;

    if (value !== "" && errors[name] !== undefined) {
      setErrors(() => ({ ...handleError(name) }));
    }
    if (value === "" && errors[name] === undefined) {
      setErrors((prev) => ({ ...prev, [name]: t("This field is required.") }));
    }

    setData((prev) => ({ ...prev, [name]: value }));
  };

  const checkApiError = (err) => {
    let message = "";
    switch (err?.code) {
      case 3001:
        if (err.more === "licenseNo") message = `${t("*Mandatory Fields can not be empty")} - ${t("Licence")}`;
        else if (err.more === "productName") message = `${t("*Mandatory Fields can not be empty")} - ${t("Product")}`;
        else if (err.more === "packName") message = `${t("*Mandatory Fields can not be empty")} - ${t("PACK")}`;
        else if (err.more === "version") message = `${t("*Mandatory Fields can not be empty")} - ${t("Version")}`;
        else if (err.more === "owner") message = `${t("*Mandatory Fields can not be empty")} - ${t("Owner")}`;
        else if (err.more === "country") message = `${t("*Mandatory Fields can not be empty")} - ${t("Country")}`;
        else message = err.message;
        break;
      default:
        message = err?.message || "Error";
    }
    return message;
  };

  const onFormSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      dispatch(setLoader({ message: t(COMMON_PROCESSING_MESSAGE) }));

      const res = await licenceService.updateLicence(data);

      if (res?.success) {
        dispatch(fetchLicenceData({ pageIndex, pageSize, filter }));
        dispatch(success({ message: t(SUCCESS_UPDATED), delay: 5000 }));
      } else {
        dispatch(error({ message: t(res?.error || "ErrorOccurred"), delay: 5000 }));
      }
    } catch (err) {
      dispatch(error({ message: checkApiError(err?.error), delay: 5000 }));
    } finally {
      dispatch(unsetLoader());
      setSubmitting(false);
    }
  };

  // const handleFileChange = (event) => {
  //   const file = event.target.files?.[0];
  //   if (!file) return;

  //   const ok = file.name.toLowerCase().endsWith(".prm");
  //   if (!ok) {
  //     setSelectedPrmName("");
  //     setBase64File("");
  //     dispatch(toastInfo({ title: t("Info"), message: t("Please select the .prm file first."), delay: 4000 }));
  //     return;
  //   }

  //   setSelectedPrmName(file.name);

  //   const reader = new FileReader();
  //   reader.onload = () => {
  //     const result = String(reader.result || "");
  //     const base64String = result.includes(",") ? result.split(",")[1] : "";
  //     setBase64File(base64String);
  //   };
  //   reader.onerror = () => {
  //     setSelectedPrmName("");
  //     setBase64File("");
  //     dispatch(toastInfo({ title: t("Info"), message: t("An error occurred while reading the file."), delay: 5000 }));
  //   };

  //   reader.readAsDataURL(file);
  // };
// const cleanPrmFileContent = (value) => {
//   let text = String(value || "")
//     .replace(/^data:.*;base64,/, "")
//     .replace(/\r/g, "")
//     .replace(/\n/g, "")
//     .replace(/\s/g, "")
//     .trim();

//   text = text.replace(/-/g, "+").replace(/_/g, "/");

//   while (text.length % 4 !== 0) {
//     text += "=";
//   }

//   return text;
// };

// const handleFileChange = (e) => {
//   const file = e.target.files?.[0];

//   if (!file) return;

//   if (!file.name.toLowerCase().endsWith(".prm")) {
//     dispatch(
//       toastInfo({
//         title: t("Info"),
//         message: t("Please select the .prm file first."),
//         delay: 4000,
//       })
//     );
//     return;
//   }

//   setSelectedPrmName(file.name);

//   const reader = new FileReader();

//   reader.onload = () => {
//     const rawText = String(reader.result || "");
//     const cleanedFile = cleanPrmFileContent(rawText);

//     console.log("PRM RAW FIRST 80:", rawText.slice(0, 80));
//     console.log("PRM CLEANED FIRST 80:", cleanedFile.slice(0, 80));
//     console.log("PRM CLEANED CHAR 26:", cleanedFile[26]);

//     setBase64File(cleanedFile);
//   };

//   reader.readAsText(file);
// };
//   const openModalAndSendFile = async () => {
//     if (!base64File) {
//       dispatch(toastInfo({ title: t("Info"), message: t("Please select the .prm file first."), delay: 4000 }));
//       return;
//     }

//     setModalLoading(true);

//     try {
//       console.log("SIGN RAW:", String(data.licenseNo).trim() + String(data.productCode).trim());
     
//       const response = await PartnerApi.postActivationRecordByFile({
//         licenseNo: data.licenseNo,
//         productCode: String(data.productCode),
//         file: base64File,
//       });

//       const licenseContent = JSON.stringify(response?.license || response, null, 2);
//       const filename = `${data.licenseNo}.lic`;

//       const blob = new Blob([licenseContent], { type: "application/octet-stream" });
//       const url = URL.createObjectURL(blob);

//       const a = document.createElement("a");
//       a.href = url;
//       a.download = filename;
//       a.click();

//       URL.revokeObjectURL(url);

//       dispatch(toastInfo({ title: t("Info"), message: t("File downloaded."), delay: 3000 }));
//     } catch (err) {
//       const msg =
//         err?.response?.data?.error?.more ||
//         err?.response?.data?.error?.message ||
//         err?.message ||
//         t("The API returned an error");

//       dispatch(toastInfo({ title: t("Info"), message: msg, delay: 6000 }));
//     } finally {
//       setModalLoading(false);
//     }
//   };
const cleanPrmFileContent = (value) => {
  let text = String(value || "").trim();

  text = text.replace(/^data:.*;base64,/, "");

  try {
    const json = JSON.parse(text);

    if (json?.file) {
      text = json.file;
    } else if (json?.data) {
      text = json.data;
    } else if (json?.license) {
      text = json.license;
    }
  } catch (_) {}

  const fileMatch = text.match(/file\s*=\s*["']?([^"'\r\n]+)["']?/i);

  if (fileMatch?.[1]) {
    text = fileMatch[1];
  }

  return String(text || "")
    .replace(/\r/g, "")
    .replace(/\n/g, "")
    .replace(/\s/g, "")
    .trim();
};

const handleFileChange = (e) => {
  const file = e.target.files?.[0];

  if (!file) return;

  if (!file.name.toLowerCase().endsWith(".prm")) {
    dispatch(
      toastInfo({
        title: t("Info"),
        message: t("Please select the .prm file first."),
        delay: 4000,
      })
    );
    return;
  }

  setSelectedPrmName(file.name);

  const reader = new FileReader();

  reader.onload = () => {
    const rawText = String(reader.result || "");
    const cleanedFile = cleanPrmFileContent(rawText);

    console.log("PRM RAW FIRST 80:", rawText.slice(0, 80));
    console.log("PRM CLEANED FIRST 80:", cleanedFile.slice(0, 80));
    console.log("PRM CLEANED LENGTH:", cleanedFile.length);
    console.log("PRM CLEANED CHAR 26:", cleanedFile.charAt(26));
    console.log("PRM CLEANED CHAR 34:", cleanedFile.charAt(34));

    setBase64File(cleanedFile);
  };

  reader.readAsText(file);
};

const openModalAndSendFile = async () => {
  if (!base64File) {
    dispatch(
      toastInfo({
        title: t("Info"),
        message: t("Please select the .prm file first."),
        delay: 4000,
      })
    );
    return;
  }

  const licenseNo = String(data?.licenseNo || "").trim();
  const productCode = String(data?.productCode || "").trim();
  const file = String(base64File || "").trim();

  setModalLoading(true);

  try {
    const response = await PartnerApi.postActivationRecordByFile({
      licenseNo,
      productCode,
      file,
    });

    const licenseContent =
      typeof response?.license === "string"
        ? response.license
        : JSON.stringify(response?.license || response, null, 2);

    const filename = `${licenseNo}.lic`;

    const blob = new Blob([licenseContent], {
      type: "application/octet-stream",
    });

    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();

    URL.revokeObjectURL(url);

    dispatch(
      toastInfo({
        title: t("Info"),
        message: t("File downloaded."),
        delay: 3000,
      })
    );
  } catch (err) {
    const msg =
      err?.response?.data?.error?.more ||
      err?.response?.data?.error?.message ||
      err?.message ||
      t("The API returned an error");

    dispatch(
      toastInfo({
        title: t("Info"),
        message: msg,
        delay: 6000,
      })
    );
  } finally {
    setModalLoading(false);
  }
};
  const tabsData = useMemo(() => data, [data]);

  return (
    <>
      <div className="flex-shrink w-full h-full">
        <TabActionButtons tabId={safeTabId}>
          <EditButtonSales data={data} />
          <EditButtonBackUp data={data} />
          <TabActivationFolderButton onClick={() => setModalVisible(true)} tooltip={t("File-Based Activation")} />
          <EditButtonSignAgreement licenseNo={data?.licenseNo} tooltip="Sign License Agreement" />
        </TabActionButtons>

        <div className="flex flex-wrap flew-row sm:justify-center h-[100%]">
          <div className="flex-shrink max-w-full px-2 w-full h-full py-4">
            <form ref={formRef} onSubmit={onFormSubmit} className="w-full overflow-auto h-full mt-1 dark:bg-gray-900 transition-colors">
              <LicenceForm data={tabsData} onChange={onChange} submitting={submitting} errors={errors} type="edit" />

              <div className="w-full sm:px-5 mt-5">
                <Tab items={items} selected={String(selected)} setSelected={(v) => setSelected(String(v))} />
                <TabHome
                  selected={selected}
                  tabs={[
                    { id: "1", component: <LicenceList data={tabsData} /> },
                    { id: "2", component: <CustomerDetailList data={tabsData} /> },
                    { id: "3", component: <InstallationDetailList data={tabsData} /> },
                    { id: "4", component: <NoteList data={tabsData} /> },
                  ]}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* <ActivationModal isOpen={modalVisible} onClose={closeModal}>
        <div className="relative">
          <div className="flex items-center justify-between px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t("Activation License File")}</h2>
            <button type="button" onClick={closeModal} aria-label="Close" className="inline-flex h-9 w-9 items-center justify-center text-gray-700 dark:text-gray-200">
              <span className="text-xl leading-none">✕</span>
            </button>
          </div>

          <div className="px-6 py-5 space-y-4">
            <input id="prm-file" type="file" accept=".prm" onChange={handleFileChange} disabled={modalLoading} className="hidden" />

            <label
              htmlFor="prm-file"
              className={`
                w-full inline-flex items-center justify-center
                h-11 rounded-lg font-semibold
                bg-blue-600 text-white hover:bg-blue-700
                dark:bg-blue-500 dark:hover:bg-blue-600
                cursor-pointer select-none
                ${modalLoading ? "opacity-60 pointer-events-none" : ""}
              `}
            >
              {t("Select File")}
            </label>

            {selectedPrmName ? (
              <div className="text-sm rounded-md px-3 py-2 bg-gray-50 text-gray-700 border border-gray-200 dark:bg-white/5 dark:text-gray-200 dark:border-white/10 break-all">
                {selectedPrmName}
              </div>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400">{t("Select the file with the .prm extension.")}</p>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={openModalAndSendFile}
                disabled={modalLoading || !selectedPrmName}
                className={`
                  h-10 px-4 rounded-lg font-semibold
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                  ${modalLoading || !selectedPrmName
                    ? "bg-gray-400 text-white cursor-not-allowed dark:bg-white/20"
                    : "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-600"
                  }
                `}
              >
                {modalLoading ? t("Loading") : t("DownloadAsLic")}
              </button>
              <button type="button" onClick={closeModal} className="h-10 px-4 rounded-lg font-semibold bg-gray-200 text-gray-800 dark:bg-white/10 dark:text-gray-100">
                {t("Close")}
              </button>
            </div>
          </div>
        </div>
      </ActivationModal> */}
      <ActivationModal isOpen={modalVisible} onClose={closeModal}>
        <div>
          <div className="relative px-5 pt-5 pb-3">
            <h2 className="text-center text-xl font-bold text-slate-900 dark:text-white">
              {t("Activation License File")}
            </h2>

            <button
              type="button"
              onClick={closeModal}
              aria-label="Close"
              className="
          absolute right-5 top-4 inline-flex h-8 w-8 items-center justify-center
          rounded-md text-slate-700 transition hover:bg-slate-100
          dark:text-white dark:hover:bg-white/10
        "
            >
              <span className="text-xl leading-none">✕</span>
            </button>
          </div>

          <div className="space-y-4 px-5 pb-5 pt-4">
            <input
              id="prm-file"
              type="file"
              accept=".prm"
              onChange={handleFileChange}
              disabled={modalLoading}
              className="hidden"
            />

            <label
              htmlFor="prm-file"
              className={`
          flex h-11 w-full items-center justify-center rounded-md
          bg-[#1d477f] font-semibold text-white
          cursor-pointer select-none transition hover:bg-[#163866]
          dark:bg-[#3b82f6] dark:hover:bg-[#2563eb]
          ${modalLoading ? "pointer-events-none opacity-60" : ""}
        `}
            >
              {t("Select File")}
            </label>

            {selectedPrmName ? (
              <div className="break-all rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 dark:border-white/20 dark:bg-white/5 dark:text-white">
                {selectedPrmName}
              </div>
            ) : (
              <p className="text-sm font-medium text-slate-700 dark:text-white/95">
                {t("Select the file with the .prm extension.")}
              </p>
            )}

            <div className="flex w-full justify-end gap-3 pt-3">
              <button
                type="button"
                onClick={closeModal}
                className="
    h-10 rounded-md
    bg-slate-300 px-5
    font-bold text-slate-800
    transition hover:bg-slate-400
    dark:bg-[#4b5563]
    dark:text-white
    dark:hover:bg-[#6b7280]
  "
              >
                {t("Close")}
              </button>
              <button
                type="button"
                onClick={openModalAndSendFile}
                disabled={modalLoading || !selectedPrmName}
                className={`
    h-10 rounded-md px-5
    font-bold transition
    ${modalLoading || !selectedPrmName
                    ? "cursor-not-allowed bg-slate-400 text-white/90 dark:bg-[#4b5563] dark:text-white/80"
                    : "bg-[#1d477f] text-white hover:bg-[#163866] dark:bg-[#3b82f6] dark:hover:bg-[#2563eb]"
                  }
  `}
              >
                {modalLoading ? t("Loading") : t("Active")}
              </button>
            </div>
          </div>
        </div>
      </ActivationModal>
    </>
  );
};

export default EditLicence;